// Radha — Overview: the receivables book's home screen.
//
// Redesigned per secondary research (fintech/SaaS dashboard UI trends, 2026):
// "trust through restraint" — one true north-star hero figure up top instead of
// a wall of equal-weight numbers (SaaSFrame's "Anatomy of high-performance SaaS
// dashboards"), a bento-grid mix of tile sizes rather than a uniform row
// (SaaSUI/Orbix bento-grid guidance), and a real trend sparkline — fed only
// genuinely stored history (Cashflow's monthly closing balance), never a
// fabricated delta. Data underneath is unchanged: still read-only, still
// computed live from the module reads (no new tables).
import React, { useMemo } from "react";

import { useAccounts } from "../hooks/useCollectionsDirect";
import { useDisputes } from "../hooks/useDisputesDirect";
import { useCrmDeals, useCashflowPeriods } from "../hooks/useBatchModules";
import { ModuleChart } from "../components/ModuleChart";
import { ExportButton } from "../components/TableSearch";
import { Sparkline } from "../components/Sparkline";
import { riskBandColor } from "../lib/statusColor";
import { TempleArch, LotusFlourish } from "../components/DevotionalMotifs";
import { ReceivablesTrend } from "../components/ReceivablesTrend";
import { DsoWaterfall } from "../components/DsoWaterfall";
import { useRole } from "../lib/authSupabase";
import type { CollectionAccount } from "../types/collections";
import { fmtMoney as inr, fmtDateShort } from "../lib/money";

const monthLabel = (m: string) => fmtDateShort(m);

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "9px 8px" };

// AR ageing buckets by days past due (Zoho Books uses the same 30-day bands).
const AGING: { key: string; hit: (dpd: number) => boolean }[] = [
  { key: "Current", hit: (d) => d <= 0 },
  { key: "1–30", hit: (d) => d >= 1 && d <= 30 },
  { key: "31–60", hit: (d) => d >= 31 && d <= 60 },
  { key: "61–90", hit: (d) => d >= 61 && d <= 90 },
  { key: "90+", hit: (d) => d > 90 },
];
const BUCKET_COLOR: Record<string, string> = {
  "Current": "var(--status-good)", "1–30": "var(--accent-purple)", "31–60": "var(--status-warning)", "61–90": "#E08A4B", "90+": "var(--status-critical)",
};

const cardPad: React.CSSProperties = { padding: "16px 18px" };

function Stat({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: string }) {
  return (
    <div title={hint}>
      <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>{label}</div>
      <div style={{ font: "600 18px Inter", color: tone ?? "var(--fg)" }}>{value}</div>
    </div>
  );
}

const OPEN_DISPUTE = new Set(["open", "under_review", "escalated"]);

export function OverviewPanel() {
  const { data: accounts, isLoading, error } = useAccounts();
  const { data: disputes } = useDisputes();
  const { data: deals } = useCrmDeals();
  const { data: cashflow } = useCashflowPeriods();
  const { canWrite } = useRole();

  const m = useMemo(() => {
    const list = (accounts ?? []) as CollectionAccount[];
    const out = (a: CollectionAccount) => Number(a.outstanding_amount) || 0;
    const total = list.reduce((s, a) => s + out(a), 0);
    const overdue = list.filter((a) => a.days_past_due > 0).reduce((s, a) => s + out(a), 0);
    const current = total - overdue;
    const expectedRecovery = list.reduce((s, a) => s + out(a) * (Number(a.recovery_probability) || 0), 0);
    const wDpd = total > 0
      ? list.reduce((s, a) => s + Math.max(0, a.days_past_due) * out(a), 0) / total
      : 0;

    const aging = AGING.map((b) => {
      const rows = list.filter((a) => b.hit(a.days_past_due));
      return { bucket: b.key, count: rows.length, amount: rows.reduce((s, a) => s + out(a), 0) };
    });

    const topDebtors = [...list].sort((a, b) => out(b) - out(a)).slice(0, 8);

    const disputeExposure = (disputes ?? [])
      .filter((d) => OPEN_DISPUTE.has(d.status))
      .reduce((s, d) => s + (Number(d.disputed_amount) || 0), 0);

    const pipeline = (deals ?? [])
      .filter((d) => d.stage !== "won" && d.stage !== "lost")
      .reduce((s, d) => s + (Number(d.weighted_value) || 0), 0);

    const cfSorted = [...(cashflow ?? [])].sort((a, b) => (a.period_month < b.period_month ? -1 : 1));
    const cashSeries = cfSorted.map((p) => Number(p.closing_balance) || 0);
    const cashDelta = cashSeries.length >= 2 ? cashSeries[cashSeries.length - 1] - cashSeries[0] : 0;
    const cashFromLabel = cfSorted.length ? monthLabel(cfSorted[0].period_month) : "";

    return { total, overdue, current, expectedRecovery, wDpd, aging, topDebtors, disputeExposure, pipeline, cashSeries, cashDelta, cashFromLabel };
  }, [accounts, disputes, deals, cashflow]);

  if (isLoading) return <p style={{ padding: 24 }}>Loading…</p>;
  if (error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(error as Error).message}</p>;

  const overduePct = m.total > 0 ? Math.round((m.overdue / m.total) * 100) : 0;

  return (
    <div>
      {/* Hero row — one north-star figure + the one trend that has real history */}
      <div className="sanctuary-stagger sanctuary-overview-hero" style={{ display: "grid", gap: 14, marginBottom: 14 }}>
        <div className="sanctuary-card sanctuary-reveal" style={{ ...cardPad, display: "flex", flexDirection: "column", justifyContent: "center", position: "relative", overflow: "hidden" }}>
          <TempleArch width={260} height={110} style={{ position: "absolute", right: -10, top: -10, color: "var(--accent-gold)", opacity: .18, pointerEvents: "none" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--fg-muted)", fontSize: 13, position: "relative" }}>
            <LotusFlourish size={14} />
            Total receivables
          </div>
          <div className="font-devotional-display sanctuary-hero-figure" style={{ letterSpacing: "0", margin: "6px 0 6px", position: "relative" }}>{inr(m.total)}</div>
          <div style={{ fontSize: 13, color: overduePct > 0 ? "var(--status-serious)" : "var(--status-good)", position: "relative" }}>
            {overduePct > 0 ? `${overduePct}% overdue (${inr(m.overdue)})` : "Nothing overdue"}
          </div>
        </div>
        <div className="sanctuary-card sanctuary-reveal" style={{ ...cardPad, display: "flex", flexDirection: "column", justifyContent: "center", gap: 6 }}>
          <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Cash position</div>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 10 }}>
            <div>
              <div style={{ font: "600 22px Inter" }}>{m.cashSeries.length ? inr(m.cashSeries[m.cashSeries.length - 1]) : "—"}</div>
              {m.cashSeries.length >= 2 && (
                <div style={{ fontSize: 12, color: m.cashDelta >= 0 ? "var(--status-good)" : "var(--status-critical)" }}>
                  {m.cashDelta >= 0 ? "+" : ""}{inr(m.cashDelta)} since {m.cashFromLabel}
                </div>
              )}
            </div>
            {m.cashSeries.length >= 2 && <Sparkline values={m.cashSeries} accent="var(--accent-purple)" />}
          </div>
        </div>
      </div>

      <ReceivablesTrend canWrite={canWrite} />
      <DsoWaterfall />

      {/* Secondary KPI row — calm, de-emphasised vs the hero */}
      <div style={{ display: "flex", gap: 26, margin: "0 0 22px", flexWrap: "wrap" }}>
        <Stat label="Expected recovery" value={inr(m.expectedRecovery)}
          hint="Outstanding × each account's recovery probability." />
        <Stat label="Avg days overdue" value={`${Math.round(m.wDpd)}d`}
          hint="Outstanding-weighted average days past due (a DSO-style ageing proxy)." />
        <Stat label="Disputed (open)" value={inr(m.disputeExposure)} tone={m.disputeExposure > 0 ? "var(--status-warning)" : undefined}
          hint="Disputed amount on open / under-review / escalated disputes." />
        <Stat label="Open pipeline" value={inr(m.pipeline)} hint="Weighted value of open CRM deals." />
      </div>

      {/* Bento row: ageing (wide) + top debtors (tall) */}
      <div className="sanctuary-stagger sanctuary-overview-bento" style={{ display: "grid", gap: 14, alignItems: "start" }}>
        <div className="sanctuary-card sanctuary-reveal" style={cardPad}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", margin: "0 0 6px" }}>
            <div style={{ font: "600 15px Inter" }}>AR ageing</div>
            <ExportButton name="AR Ageing" rows={() => m.aging.map((b) => ({
              Bucket: b.bucket, Accounts: b.count, Outstanding: b.amount,
              "Share %": m.total > 0 ? Math.round((b.amount / m.total) * 100) : 0,
            }))} />
          </div>
          <ModuleChart title="Outstanding by ageing bucket" money
            data={m.aging.map((b) => ({ label: b.bucket, value: b.amount }))}
            colorMap={BUCKET_COLOR} />
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
                <th style={th}>Bucket</th><th style={th}>Accounts</th><th style={th}>Outstanding</th><th style={th}>Share</th>
              </tr>
            </thead>
            <tbody>
              {m.aging.map((b) => (
                <tr key={b.bucket} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ ...td, color: BUCKET_COLOR[b.bucket], fontWeight: 600 }}>{b.bucket}</td>
                  <td style={td}>{b.count}</td>
                  <td style={td}>{inr(b.amount)}</td>
                  <td style={{ ...td, color: "var(--fg-muted)" }}>{m.total > 0 ? `${Math.round((b.amount / m.total) * 100)}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="sanctuary-card sanctuary-reveal" style={cardPad}>
          <div style={{ font: "600 15px Inter", margin: "0 0 10px" }}>Top debtors</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
                <th style={th}>Debtor</th><th style={{ ...th, textAlign: "right" }}>Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {m.topDebtors.map((a) => (
                <tr key={a.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ ...td, paddingRight: 4 }}>
                    <div>
                      {a.debtor_name}
                      {a.samadhaan_eligible && <span style={{ marginLeft: 6, fontSize: 10, color: "var(--status-warning)", border: "1px solid var(--status-warning)", borderRadius: 6, padding: "0 4px" }}>§16</span>}
                    </div>
                    <div style={{ fontSize: 11, color: riskBandColor(a.risk_band), fontWeight: a.risk_band === "critical" || a.risk_band === "high" ? 600 : 400 }}>
                      {a.days_past_due}d · <span style={{ textTransform: "capitalize" }}>{a.risk_band}</span>
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>{inr(a.outstanding_amount)}</td>
                </tr>
              ))}
              {m.topDebtors.length === 0 && (
                <tr><td style={{ ...td, color: "var(--fg-muted)" }} colSpan={2}>No receivables yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
